import { Injectable } from '@angular/core';
import { LanguageMap, WEBFLOW_TOUR_LOCALES, WebflowTourLocale } from '@motes-sdk/models';
import { Mote } from '@motes-sdk/models';
import { WebflowService, wfTour } from './webflow.service';

/**
 * Google Things to Do feed format interface
 * Based on: https://developers.google.com/actions-center/verticals/things-to-do/guides/partner-integration/required-fields
 */
export interface GoogleTTDFeed {
    feed_metadata: GoogleTTDFeedMetadata;
    products: GoogleTTDProduct[];
}

export interface GoogleTTDFeedMetadata {
    shard_id: number;
    total_shards_count: number;
    processing_instruction: string; // "PROCESS_AS_SNAPSHOT" or "PROCESS_AS_UPSERT"
    nonce: number;
}

export interface GoogleTTDLocalizedText {
    language_code: string;
    text: string;
}

export interface GoogleTTDLocalizedTextWrapper {
    localized_texts: GoogleTTDLocalizedText[];
}

export interface GoogleTTDProduct {
    id: string;
    title: GoogleTTDLocalizedTextWrapper;
    description?: GoogleTTDLocalizedTextWrapper;
    rating?: {
        average_value: number;
        rating_count: number;
    };
    product_features?: GoogleTTDProductFeature[];
    related_media?: GoogleTTDMedia[];
    options: GoogleTTDOption[];
    operator?: GoogleTTDOperator;
    brand_name?: GoogleTTDLocalizedTextWrapper; // Required for non-OTA partners
    confirmation_type?: string; // e.g., "CONFIRMATION_TYPE_INSTANT"
    fulfillment_type?: {
        mobile?: boolean;
        print_at_home?: boolean;
        pickup?: boolean;
    };
}

export interface GoogleTTDProductFeature {
    feature_type: string; // e.g., "TEXT_FEATURE_INCLUSION", "TEXT_FEATURE_HIGHLIGHT", "TEXT_FEATURE_MUST_KNOW"
    value: GoogleTTDLocalizedTextWrapper;
}

export interface GoogleTTDOption {
    id: string;
    title: GoogleTTDLocalizedTextWrapper;
    description?: GoogleTTDLocalizedTextWrapper;
    landing_page: {
        url: string;
        localized_url?: GoogleTTDLocalizedTextWrapper;
    };
    landing_page_list_view?: {
        url: string;
        localized_url?: GoogleTTDLocalizedTextWrapper;
    };
    duration_sec?: number;
    cancellation_policy?: {
        refund_conditions: Array<{
            min_duration_before_start_time_sec: number;
            refund_percent: number;
        }>;
    };
    option_categories?: Array<{
        label: string;
    }>;
    related_locations?: GoogleTTDRelatedLocation[];
    price_options: GoogleTTDPriceOption[];
    meeting_point?: {
        location: {
            place_id?: string;
        };
        description?: GoogleTTDLocalizedTextWrapper;
    };
}

export interface GoogleTTDPriceOption {
    id?: string;
    title?: string;
    price?: {
        currency_code: string;
        units: number;
        nanos?: number;
    };
    is_free?: boolean;
    fees_and_taxes?: {
        per_ticket_fee?: {
            currency_code: string;
            units: number;
        };
        per_ticket_tax?: {
            currency_code: string;
            units: number;
        };
    };
}

export interface GoogleTTDRelatedLocation {
    location: {
        location: {
            place_id?: string;
            place_info?: {
                name?: string;
                coordinates?: {
                    latitude: number;
                    longitude: number;
                };
            };
        };
    };
    relation_type: 'RELATION_TYPE_RELATED_NO_ADMISSION' | 'RELATION_TYPE_ADMISSION_TICKET' | 'RELATION_TYPE_SUPPLEMENTARY_ADDON'
}

export interface GoogleTTDMedia {
    url: string;
    type: string; // e.g., "MEDIA_TYPE_PHOTO", "MEDIA_TYPE_VIDEO"
    attribution?: GoogleTTDLocalizedTextWrapper;
}

export interface GoogleTTDOperator {
    name?: GoogleTTDLocalizedTextWrapper;
    phone_number?: string;
    google_business_profile_name?: GoogleTTDLocalizedTextWrapper;
    locations?: Array<{
        location: {
            place_id?: string;
            place_info?: {
                name?: string;
                phone_number?: string;
                website_url?: string;
                unstructured_address?: string;
            };
        };
    }>;
}

/** Locales attached to an English product when content is a real translation. */
const TTD_ATTACH_LOCALES: WebflowTourLocale[] = ['da', 'de'];

type FeatureKey = 'selfGuided' | 'storyCount' | 'tourLanguages' | 'quiz' | 'duration' | 'free';

@Injectable({
    providedIn: 'root'
})
export class TtdExportService {

    constructor(private webflowService: WebflowService) {}

    /**
     * Main POI only for TTD related_locations (Firebase placeId, else Webflow place-id).
     * Child relatedPlaceIds are intentionally omitted to avoid overtagging / bad matches.
     */
    private buildTtdRelatedPlaceIds(firebaseTour?: Mote, webflowPlaceId?: string): string[] {
        const main = firebaseTour?.placeId?.trim() || webflowPlaceId?.trim();
        return main ? [main] : [];
    }

    /**
     * Check if a tour's cover image is valid for TTD export
     * Cover image is valid if it exists and doesn't have text
     * @param tour Webflow tour
     * @param firebaseTour Optional Firebase tour to check for imageHasText metadata
     * @returns True if cover image is valid
     */
    public hasValidCoverImage(tour: wfTour, firebaseTour?: Mote): boolean {
        // Cover image must exist in Webflow
        if (!tour.fieldData['cover-image']?.url) {
            return false;
        }
        
        // Cover image must not have text (checked via Firebase metadata)
        if (firebaseTour?.image?.imageHasText === true) {
            return false;
        }
        
        return true;
    }

    /**
     * Count the number of valid images for a tour that will be included in TTD export
     * Valid images are: cover image (if no text) + extra images
     * @param tour Webflow tour
     * @param firebaseTour Optional Firebase tour to check for imageHasText metadata
     * @returns Number of valid images (0 means tour should not be exported)
     */
    public countValidImages(tour: wfTour, firebaseTour?: Mote): number {
        let count = 0;
        
        // Count cover image if valid
        if (this.hasValidCoverImage(tour, firebaseTour)) {
            count++;
        }
        
        // Count extra images (all extra images are valid if they exist in Webflow)
        // Note: Auto-generated images are already filtered out when exporting to Webflow
        const extraImagesCount = tour.fieldData['extra-tour-images']?.length || 0;
        count += extraImagesCount;
        
        return count;
    }

    /**
     * Export tours to Google Things to Do feed format
     * @param webflowTours Array of Webflow tours to export (English / primary locale)
     * @param firebaseTours Optional array of Firebase tours to check for imageHasText metadata
     * @returns The generated Google TTD feed
     */
    public async exportToursToTTD(webflowTours: wfTour[], firebaseTours?: Mote[]): Promise<GoogleTTDFeed> {
        try {
            // Filter tours: only non-hidden English tours
            const filteredTours = webflowTours.filter(tour => 
                tour.fieldData['hide-tour'] === false && 
                tour.fieldData['language'] === 'en' &&
                tour.fieldData['place-id']
            );
            
            // Create a map of Firebase tours by key for quick lookup
            const firebaseToursMap: { [key: string]: Mote } = {};
            if (firebaseTours) {
                firebaseTours.forEach(tour => {
                    firebaseToursMap[tour.key] = tour;
                });
            }
            
            // Filter tours: only export tours with at least one valid image
            const toursWithValidImages = filteredTours.filter(tour => {
                const firebaseTour = firebaseToursMap[tour.fieldData.key];
                const hasValidImages = this.countValidImages(tour, firebaseTour) > 0;
                if(!hasValidImages) {
                    console.log('🔍 Tour', tour.fieldData.key, 'has no valid images');
                }
                return hasValidImages;
            });

            // Bulk-fetch da/de locale lists (3rd call is EN already loaded for the admin UI)
            const [daTours, deTours] = await Promise.all([
                this.webflowService.getWebflowTours('da'),
                this.webflowService.getWebflowTours('de'),
            ]);
            const localeToursById: Record<WebflowTourLocale, Map<string, wfTour>> = {
                en: new Map(),
                da: this.indexToursByWebflowId(daTours),
                de: this.indexToursByWebflowId(deTours),
            };
            
            // Map all tours to Google Things to Do format
            const googleTTDProducts: GoogleTTDProduct[] = toursWithValidImages
                .map(tour => {
                    const firebaseTour = firebaseToursMap[tour.fieldData.key];
                    const locales = this.resolveEligibleLocales(tour, localeToursById);
                    return this.mapWebflowTourToGoogleTTD(tour, firebaseTour, locales);
                });
            
            console.log('🔍 Google TTD Products:', googleTTDProducts);
            
            // Create feed metadata
            const feedMetadata: GoogleTTDFeedMetadata = {
                shard_id: 0,
                total_shards_count: 1,
                processing_instruction: 'PROCESS_AS_SNAPSHOT', // String format as per JSON examples
                nonce: Date.now() // Use current timestamp as nonce
            };

            // Create the complete feed
            const feed: GoogleTTDFeed = {
                feed_metadata: feedMetadata,
                products: googleTTDProducts
            };

            // Convert to JSON and download
            const jsonString = JSON.stringify(feed, null, 2);
            const blob = new Blob([jsonString], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Generate filename in format: DD-MM-YY-HH.MM-ttd-feed.json
            link.download = this.generateTTDFeedFilename();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            console.log('✅ Google TTD feed exported successfully', feed);
            return feed;
        } catch (error) {
            console.error('Error exporting tours to Google TTD:', error);
            throw error;
        }
    }

    private indexToursByWebflowId(tours: wfTour[]): Map<string, wfTour> {
        const map = new Map<string, wfTour>();
        for (const tour of tours) {
            if (tour.id) {
                map.set(tour.id, tour);
            }
        }
        return map;
    }

    /**
     * Eligible da/de variants: language matches locale, long description differs from EN, not hidden.
     */
    private resolveEligibleLocales(
        enTour: wfTour,
        localeToursById: Record<WebflowTourLocale, Map<string, wfTour>>,
    ): Partial<Record<WebflowTourLocale, wfTour>> {
        const eligible: Partial<Record<WebflowTourLocale, wfTour>> = {};
        if (!enTour.id) {
            return eligible;
        }

        const enLong = this.normalizeDescriptionForCompare(enTour.fieldData['long-description-rich']);

        for (const locale of TTD_ATTACH_LOCALES) {
            const localeTour = localeToursById[locale].get(enTour.id);
            if (!localeTour) {
                continue;
            }
            if (localeTour.fieldData['hide-tour'] !== false) {
                continue;
            }
            if ((localeTour.fieldData.language || '').toLowerCase() !== locale) {
                continue;
            }
            const localeLong = this.normalizeDescriptionForCompare(localeTour.fieldData['long-description-rich']);
            if (!localeLong || localeLong === enLong) {
                continue;
            }
            eligible[locale] = localeTour;
        }

        return eligible;
    }

    /** Strip HTML + collapse whitespace for uniqueness comparison. */
    private normalizeDescriptionForCompare(html: string | undefined | null): string {
        if (!html) {
            return '';
        }
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        return (tempDiv.textContent || '')
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase();
    }

    /**
     * Create localized text wrapper from one or more language entries
     */
    private createLocalizedText(entries: Array<{ languageCode: string; text: string }>): GoogleTTDLocalizedTextWrapper {
        return {
            localized_texts: entries
                .filter(entry => !!entry.text)
                .map(entry => ({
                    language_code: entry.languageCode,
                    text: entry.text
                }))
        };
    }

    private featureText(
        key: FeatureKey,
        languageCode: WebflowTourLocale,
        vars: { storyCount?: number; languageNames?: string; durationHours?: number; startPlace?: string } = {},
    ): string {
        const storyCount = vars.storyCount ?? 0;
        const languageNames = vars.languageNames ?? '';
        const durationHours = vars.durationHours ?? 0;
        const startPlace = vars.startPlace ?? '';

        const map: Record<FeatureKey, Record<WebflowTourLocale, string>> = {
            selfGuided: {
                en: 'Self-guided audio walk – go at your own pace',
                da: 'Selvguidet lydvandring – gå i dit eget tempo',
                de: 'Selbstgeführte Audio-Tour – in Ihrem eigenen Tempo',
            },
            storyCount: {
                en: `Listen to ${storyCount} stories on the tour – also available as text`,
                da: `Lyt til ${storyCount} historier på turen – også tilgængelige som tekst`,
                de: `Hören Sie ${storyCount} Geschichten auf der Tour – auch als Text verfügbar`,
            },
            tourLanguages: {
                en: `Tour available in ${languageNames}`,
                da: `Tur tilgængelig på ${languageNames}`,
                de: `Tour verfügbar auf ${languageNames}`,
            },
            quiz: {
                en: 'Interactive Quiz included – fun with groups',
                da: 'Interaktiv quiz inkluderet – sjovt i grupper',
                de: 'Interaktives Quiz inklusive – Spaß in Gruppen',
            },
            duration: {
                en: `Duration: ${durationHours} hour${durationHours !== 1 ? 's' : ''} – starts at ${startPlace}`,
                da: `Varighed: ${durationHours} time${durationHours !== 1 ? 'r' : ''} – starter ved ${startPlace}`,
                de: `Dauer: ${durationHours} Stunde${durationHours !== 1 ? 'n' : ''} – Start bei ${startPlace}`,
            },
            free: {
                en: '100 % free tour audio tour',
                da: '100 % gratis lydtur',
                de: '100 % kostenlose Audio-Tour',
            },
        };

        return map[key][languageCode];
    }

    private languageNamesForLocale(languageCodes: string[], locale: WebflowTourLocale): string {
        return languageCodes
            .map(code => {
                const option = LanguageMap[code as keyof typeof LanguageMap];
                if (!option) {
                    return code;
                }
                // EN uses English names; da/de use native labels from LanguageMap
                return locale === 'en' ? option.labelEn : option.label;
            })
            .join(', ');
    }

    /**
     * Sanitize HTML to only allow tags supported by Google Things to Do
     * Allowed tags: h1-h5, ul, ol, li, strong, italic, em, p, br
     * Based on: https://developers.google.com/actions-center/verticals/things-to-do/reference/feed-spec/product-feed#implementation-notes_19
     */
    private sanitizeHtmlForDescription(html: string): string {
        if (!html) return html;

        // Create a temporary DOM element to parse HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        // List of allowed tags (per Google TTD spec: h1-h5, ul, ol, li, strong, italic, em, p, br)
        // Note: "italic" refers to <i> tag in HTML
        const allowedTags = ['h1', 'h2', 'h3', 'h4', 'h5', 'ul', 'ol', 'li', 'strong', 'i', 'em', 'p', 'br'];

        /**
         * Recursively sanitize nodes
         */
        const sanitizeNode = (node: Node): void => {
            if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as Element;
                const tagName = element.tagName.toLowerCase();

                if (!allowedTags.includes(tagName)) {
                    // Replace disallowed tags with their text content
                    const textContent = element.textContent || '';
                    const textNode = document.createTextNode(textContent);
                    if (element.parentNode) {
                        element.parentNode.replaceChild(textNode, element);
                    }
                    return;
                }

                // Remove all attributes from allowed tags (CSS, style, links, etc. are not supported)
                const attrsToRemove: string[] = [];
                for (let i = 0; i < element.attributes.length; i++) {
                    attrsToRemove.push(element.attributes[i].name);
                }
                attrsToRemove.forEach(attr => element.removeAttribute(attr));

                // Recursively sanitize children (work backwards to avoid index issues)
                const children = Array.from(element.childNodes);
                for (let i = children.length - 1; i >= 0; i--) {
                    sanitizeNode(children[i]);
                }
            }
        };

        // Sanitize all nodes (work backwards to avoid index issues)
        const children = Array.from(tempDiv.childNodes);
        for (let i = children.length - 1; i >= 0; i--) {
            sanitizeNode(children[i]);
        }

        return tempDiv.innerHTML;
    }

    /**
     * Map a Webflow tour to Google Things to Do product format
     * Based on: https://developers.google.com/actions-center/verticals/things-to-do/guides/partner-integration/required-fields
     * @param tour English Webflow tour to map
     * @param firebaseTour Optional Firebase tour to check for imageHasText metadata
     * @param locales Eligible localized Webflow tours keyed by locale
     */
    private mapWebflowTourToGoogleTTD(
        tour: wfTour,
        firebaseTour?: Mote,
        locales: Partial<Record<WebflowTourLocale, wfTour>> = {},
    ): GoogleTTDProduct {
        const fd = tour.fieldData;
        const languageCode: WebflowTourLocale = 'en';
        const localeEntries = TTD_ATTACH_LOCALES
            .filter(locale => !!locales[locale])
            .map(locale => ({ locale, tour: locales[locale]! }));
        
        // Generate landing page URL using base URL
        const landingPageUrl = `${WEBFLOW_TOUR_LOCALES.en.baseUrl}${fd.slug}`;
        const localizedUrls = localeEntries.map(({ locale, tour: localeTour }) => ({
            languageCode: locale,
            text: `${WEBFLOW_TOUR_LOCALES[locale].baseUrl}${localeTour.fieldData.slug}`,
        }));
        
        // Build related media array from cover image and extra images
        const relatedMedia: GoogleTTDMedia[] = [];
        // Only include cover image if it's valid (exists and doesn't have text)
        if (this.hasValidCoverImage(tour, firebaseTour)) {
            relatedMedia.push({
                url: this.toJpeg(fd['cover-image'].url),
                type: 'MEDIA_TYPE_PHOTO'
            });
        }
        // Always include extra images (they're not checked for text)
        if (fd['extra-tour-images']?.length) {
            fd['extra-tour-images'].forEach(img => {
                if (img.url) {
                    relatedMedia.push({
                        url: this.toJpeg(img.url),
                        type: 'MEDIA_TYPE_PHOTO'
                    });
                }
            });
        }

        // Build price options - convert price to units (assuming price is in DKK, whole numbers)
        const isFree = fd['is-free'] || false;
        const priceOption: GoogleTTDPriceOption = {
            id: `${fd.key}-price-1`,
            title: 'Standard'
        };
        
        if (isFree) {
            // For free products, set is_free and omit price field
            priceOption.is_free = true;
        } else {
            // For paid products, include price field
            priceOption.price = {
                currency_code: 'DKK',
                units: Math.round(fd.price),
                nanos: 0
            };
        }
        
        const priceOptions: GoogleTTDPriceOption[] = [priceOption];

        const relatedPlaceIds = this.buildTtdRelatedPlaceIds(firebaseTour, fd['place-id']);
        const relatedLocations: GoogleTTDRelatedLocation[] = relatedPlaceIds.map(placeId => ({
            location: {
                location: {
                    place_id: placeId
                }
            },
            relation_type: 'RELATION_TYPE_RELATED_NO_ADMISSION'
        }));

        const featureLocales: WebflowTourLocale[] = [languageCode, ...localeEntries.map(e => e.locale)];

        const buildFeature = (
            key: FeatureKey,
            varsBuilder?: (locale: WebflowTourLocale) => { storyCount?: number; languageNames?: string; durationHours?: number; startPlace?: string },
        ): GoogleTTDProductFeature => ({
            feature_type: 'TEXT_FEATURE_INCLUSION',
            value: this.createLocalizedText(
                featureLocales.map(locale => ({
                    languageCode: locale,
                    text: this.featureText(key, locale, varsBuilder ? varsBuilder(locale) : {}),
                }))
            ),
        });

        // Build product features with localized texts
        const productFeatures: GoogleTTDProductFeature[] = [];
        
        productFeatures.push(buildFeature('selfGuided'));
        productFeatures.push(buildFeature('storyCount', () => ({ storyCount: fd['story-count'] })));

        if (fd.tourlanguages) {
            const languageCodes = fd.tourlanguages.includes(',') 
                ? fd.tourlanguages.split(',').map(l => l.trim()) 
                : [fd.tourlanguages];
            productFeatures.push(buildFeature('tourLanguages', (locale) => ({
                languageNames: this.languageNamesForLocale(languageCodes, locale),
            })));
        }
        if (fd['has-quiz']) {
            productFeatures.push(buildFeature('quiz'));
        }
        if (fd['duration-2']) {
            productFeatures.push(buildFeature('duration', (locale) => {
                const startPlace = locale === 'en'
                    ? fd['start-place']
                    : (locales[locale]?.fieldData['start-place'] || fd['start-place']);
                return {
                    durationHours: fd['duration-2'],
                    startPlace,
                };
            }));
        }
        if (fd['is-free']) {
            productFeatures.push(buildFeature('free'));
        }

        // Build option categories
        const optionCategories: Array<{ label: string }> = [
            { label: 'audio-guide' }
        ];

        // Convert duration from hours to seconds (round to integer to avoid floating point precision issues)
        const durationSec = fd['duration-2'] ? Math.round(fd['duration-2'] * 3600) : undefined;

        const titleEntries = [
            { languageCode, text: fd.name },
            ...localeEntries.map(({ locale, tour: localeTour }) => ({
                languageCode: locale,
                text: localeTour.fieldData.name,
            })),
        ];

        const shortDescriptionEntries = [
            ...(fd['short-description'] ? [{ languageCode, text: fd['short-description'] }] : []),
            ...localeEntries
                .filter(({ tour: localeTour }) => !!localeTour.fieldData['short-description'])
                .map(({ locale, tour: localeTour }) => ({
                    languageCode: locale,
                    text: localeTour.fieldData['short-description'],
                })),
        ];

        const longDescriptionEntries = [
            ...(fd['long-description-rich']
                ? [{ languageCode, text: this.sanitizeHtmlForDescription(fd['long-description-rich']) }]
                : []),
            ...localeEntries
                .filter(({ tour: localeTour }) => !!localeTour.fieldData['long-description-rich'])
                .map(({ locale, tour: localeTour }) => ({
                    languageCode: locale,
                    text: this.sanitizeHtmlForDescription(localeTour.fieldData['long-description-rich']),
                })),
        ];

        // Create the main option
        const option: GoogleTTDOption = {
            id: `${fd.key}-option-1`,
            title: this.createLocalizedText(titleEntries),
            description: shortDescriptionEntries.length > 0
                ? this.createLocalizedText(shortDescriptionEntries)
                : undefined,
            landing_page: {
                url: landingPageUrl,
                ...(localizedUrls.length > 0
                    ? { localized_url: this.createLocalizedText(localizedUrls) }
                    : {}),
            },
            duration_sec: durationSec,
            cancellation_policy: {
                refund_conditions: [{
                    min_duration_before_start_time_sec: 86400, // 24 hours
                    refund_percent: 100
                }]
            },
            option_categories: optionCategories,
            related_locations: relatedLocations.length > 0 ? relatedLocations : undefined,
            price_options: priceOptions
        };

        const brandEntries = featureLocales.map(locale => ({
            languageCode: locale,
            text: 'StoryHunt',
        }));

        // Build the product
        const product: GoogleTTDProduct = {
            id: fd.key, // Use Firebase key (already validated/sanitized)
            title: this.createLocalizedText(titleEntries),
            description: longDescriptionEntries.length > 0
                ? this.createLocalizedText(longDescriptionEntries)
                : undefined,
            rating: fd.rating ? {
                average_value: fd.rating,
                rating_count: fd['rating-count'] || 0
            } : undefined,
            product_features: productFeatures.length > 0 ? productFeatures : undefined,
            related_media: relatedMedia.length > 0 ? relatedMedia : undefined,
            options: [option],
            brand_name: this.createLocalizedText(brandEntries), // Required for non-OTA partners
            confirmation_type: 'CONFIRMATION_TYPE_INSTANT',
            fulfillment_type: {
                mobile: true,
                print_at_home: false,
                pickup: false
            }
        };

        return product;
    }

    /**
     * Convert image URL to JPEG format using ImageKit
     */
    private toJpeg(url: string): string {
        return url.replace(
            'https://cdn.prod.website-files.com/',
            'https://ik.imagekit.io/storyhunt/alt/tr:f-jpg,w-1400/'
        );
    }

    /**
     * Generate filename for TTD feed export
     * Format: DD-MM-YY-HH.MM-ttd-feed.json
     * Example: 13-11-25-10.21-ttd-feed.json
     */
    private generateTTDFeedFilename(): string {
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = String(now.getFullYear()).slice(-2);
        const hour = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        
        return `${day}-${month}-${year}-${hour}.${minutes}-ttd-feed.json`;
    }
}
